"use client";

import jwtDecode from "jwt-decode";
import { useCallback, useEffect, useState } from "react";

import { type DecodedAccessToken, usePassportProvider } from "@/context";
import { notifyError } from "@/utils/monitoring";

export function useAccessToken(): () => Promise<string | undefined> {
  const { logout, passportClient } = usePassportProvider();

  const getAccessToken = useCallback(async () => {
    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        return await passportClient.getAccessToken();
      } catch (e) {
        // If this is reached there was an error silently refreshing inside the Passport SDK and a re-login is required
        notifyError(e, "useAccessToken");

        if (
          e instanceof Error &&
          e.message.includes("Unknown or invalid refresh token")
        ) {
          break;
        }

        retryCount++;
      }
    }

    logout();
    return undefined;
  }, [passportClient, logout]);

  return getAccessToken;
}

// This is a hook that returns the current access token. It will automatically refresh the token if it is expired.
// Doesnt rely on you calling it in a useEffect, it will always return the current token or undefined unwrapped from annoying promises.
export const useAccessTokenState = () => {
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const { passportClient, passportState } = usePassportProvider();

  // Retry in 5 minutes on failure
  const RETRY_FAILURE_MS = 300_000;
  // Buffer to refresh before expiry
  const BUFFER_MS = 120_000; // 2 minutes

  useEffect(() => {
    if (!passportClient || !passportState.authenticated) {
      setAccessToken(undefined);
      return;
    }

    let isMounted = true;
    let timeoutID: number;

    const scheduleNextFetch = (delayMs: number) => {
      if (isMounted) {
        timeoutID = setTimeout(fetchToken, delayMs);
      }
    };

    const fetchToken = async () => {
      try {
        const token = await passportClient.getAccessToken();
        if (!isMounted || !token) {
          setAccessToken(undefined);
          scheduleNextFetch(RETRY_FAILURE_MS);
          return;
        }
        const decoded = jwtDecode<DecodedAccessToken>(token);
        const now = Date.now() / 1000;

        const isValid = decoded.exp > now;
        const nextFetchDelay = isValid
          ? Math.max((decoded.exp - now) * 1000 - BUFFER_MS, 0)
          : RETRY_FAILURE_MS;

        setAccessToken(isValid ? token : undefined);
        scheduleNextFetch(nextFetchDelay);
      } catch (error) {
        console.error("Failed to decode access token", error);
        setAccessToken(undefined);
        scheduleNextFetch(RETRY_FAILURE_MS);
      }
    };

    fetchToken();

    return () => {
      // Prevent state updates after dismounting
      isMounted = false;
      if (timeoutID) {
        clearTimeout(timeoutID);
      }
    };
  }, [passportClient, passportState.authenticated]);

  return accessToken;
};
